(() => {
  const elementName = 'modish-high-variant-card';

  if (customElements.get(elementName)) return;

  const resolvedFragments = new Map();
  const requestQueue = [];
  const maxConcurrentRequests = 4;
  let activeRequests = 0;
  let cardInstanceSequence = 0;

  const uniquifyFragmentIds = (fragment) => {
    const suffix = `--modish-card-${++cardInstanceSequence}`;
    const idMap = new Map();

    fragment.querySelectorAll('[id]').forEach((element) => {
      const oldId = element.id;
      const newId = `${oldId}${suffix}`;
      idMap.set(oldId, newId);
      element.id = newId;
    });

    if (!idMap.size) return;

    const tokenAttributes = ['for', 'aria-controls', 'aria-describedby', 'aria-labelledby', 'aria-owns', 'form', 'list'];
    fragment.querySelectorAll('*').forEach((element) => {
      tokenAttributes.forEach((attribute) => {
        const value = element.getAttribute(attribute);
        if (!value) return;

        const updated = value
          .split(/\s+/)
          .map((token) => idMap.get(token) || token)
          .join(' ');
        if (updated !== value) element.setAttribute(attribute, updated);
      });

      ['href', 'xlink:href'].forEach((attribute) => {
        const value = element.getAttribute(attribute);
        if (!value?.startsWith('#')) return;
        const newId = idMap.get(value.slice(1));
        if (newId) element.setAttribute(attribute, `#${newId}`);
      });

      Array.from(element.attributes).forEach((attribute) => {
        let updated = attribute.value;
        idMap.forEach((newId, oldId) => {
          updated = updated.split(`url(#${oldId})`).join(`url(#${newId})`);
        });
        if (updated !== attribute.value) element.setAttribute(attribute.name, updated);
      });
    });
  };

  const drainQueue = () => {
    while (activeRequests < maxConcurrentRequests && requestQueue.length) {
      const job = requestQueue.shift();
      activeRequests += 1;

      job
        .run()
        .then(job.resolve, job.reject)
        .finally(() => {
          activeRequests -= 1;
          drainQueue();
        });
    }
  };

  const enqueue = (run) =>
    new Promise((resolve, reject) => {
      requestQueue.push({ run, resolve, reject });
      drainQueue();
    });

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        entry.target.hydrate();
      });
    },
    { rootMargin: '500px 0px' }
  );

  class ModishHighVariantCard extends HTMLElement {
    connectedCallback() {
      if (this.dataset.hydrated === 'true' || this.pending) return;
      observer.observe(this);
    }

    disconnectedCallback() {
      observer.unobserve(this);
      this.abortController?.abort();
    }

    async hydrate() {
      if (this.pending || this.dataset.hydrated === 'true') return;

      const productUrl = this.dataset.productUrl;
      const optionValueId = this.dataset.optionValueId;

      if (!productUrl || !optionValueId) {
        this.removeAttribute('aria-busy');
        this.dataset.hydrationError = 'true';
        return;
      }

      this.pending = true;
      this.abortController = new AbortController();

      try {
        const url = new URL(productUrl, window.location.origin);
        url.searchParams.set('section_id', 'modish-variant-card-data');
        url.searchParams.set('option_values', optionValueId);

        ['preview_theme_id', '_fd', 'pb'].forEach((parameter) => {
          const value = new URL(window.location.href).searchParams.get(parameter);
          if (value) url.searchParams.set(parameter, value);
        });

        const cacheKey = url.toString();
        let fragmentHtml = resolvedFragments.get(cacheKey);

        if (!resolvedFragments.has(cacheKey)) {
          fragmentHtml = await enqueue(async () => {
            const response = await fetch(cacheKey, {
              credentials: 'same-origin',
              headers: { Accept: 'text/html' },
              signal: this.abortController.signal,
            });

            if (!response.ok) throw new Error(`Variant card request failed: ${response.status}`);

            const responseHtml = await response.text();
            const responseDocument = new DOMParser().parseFromString(responseHtml, 'text/html');
            if (responseDocument.querySelector('[data-modish-variant-card-error="unavailable"]')) {
              resolvedFragments.set(cacheKey, null);
              return null;
            }
            const fragment = responseDocument.querySelector('[data-modish-variant-card-fragment]');

            if (!fragment) throw new Error('Variant card fragment is missing');

            const html = fragment.innerHTML.trim();
            if (!html) throw new Error('Variant card fragment is empty');

            resolvedFragments.set(cacheKey, html);
            return html;
          });
        }

        if (!this.isConnected) return;

        if (fragmentHtml === null) {
          this.dataset.catalogHidden = 'true';
          this.removeAttribute('aria-busy');
          const cardWrapper = this.closest('.main_search_item, .search_result_product_card');
          (cardWrapper || this).remove();
          return;
        }

        const template = document.createElement('template');
        template.innerHTML = fragmentHtml;
        const fragment = template.content.cloneNode(true);
        uniquifyFragmentIds(fragment);
        this.replaceChildren(fragment);
        this.dataset.hydrated = 'true';
        this.removeAttribute('aria-busy');
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.dataset.hydrationError = 'true';
          this.removeAttribute('aria-busy');
        }
      } finally {
        this.pending = false;
        this.abortController = null;

        if (this.isConnected && this.dataset.hydrated !== 'true' && !this.dataset.hydrationError) {
          observer.observe(this);
        }
      }
    }
  }

  customElements.define(elementName, ModishHighVariantCard);
})();
